import { useNavigate } from '@tanstack/react-router';
import { DetailedHTMLProps, HTMLAttributes, memo } from 'react';
import { MenuTrigger } from 'react-aria-components';

import { merge } from '@/components';
import { Button, Heading, Menu, MenuItem, Popover, Separator, Text } from '@/components/core';
import {
  AddIcon,
  AddMembersIcon,
  CheckmarkIcon,
  ChevronIcon,
  LogoutIcon,
  ProfileSettingsIcon,
  SettingsIcon,
} from '@/components/icons';
import { OrganizationLogo } from '@/components/logos';
import { useNewOrganizations } from '@/hooks';
import { toOrganizationId, useGetOrganization, useListOrganizations } from '@/hooks/api';
import { DivProps } from '@/utils';

export type WorkspaceCardProps = DivProps & {
  organizationId: string;
};

const IWorkspaceCard = ({ organizationId, className }: WorkspaceCardProps) => {
  const { data: organization } = useGetOrganization(toOrganizationId(organizationId));
  const { data: organizationList } = useNewOrganizations(useListOrganizations());
  const navigate = useNavigate();

  if (!organization) return null;

  return (
    <div className={merge('flex flex-row items-center gap-2 px-2', className)}>
      <MenuTrigger>
        <Button variant="plain" className="w-full justify-start px-0">
          <OrganizationLogo size="md" organizationId={organizationId} />
          <Heading className="truncate">
            <Text>{organization.name || 'My organization'}</Text>
          </Heading>
          <ChevronIcon rotation="down" />
        </Button>

        <Popover className="w-64 shadow-md">
          <Menu className="w-full" selectionMode="single" selectedKeys={[organization.organizationId]}>
            {organizationList?.data?.map((o) => (
              <MenuItem
                id={o.organizationId}
                key={o.organizationId}
                onAction={async () => {
                  navigate({ to: '/login', search: { orgCode: o.code }});
                }}
              >
                <OrganizationLogo organizationId={o.organizationId} />
                <Text>{o.name}</Text>
                <div className="grow" />
                {o.organizationId === organization.organizationId ? (
                  <>
                    <div className="grow" />
                    <CheckmarkIcon variant="success" />
                  </>
                ) : null}
              </MenuItem>
            ))}
            <MenuItem
              onAction={async () => {
                await navigate({ to: '/organizations/new' });
              }}
            >
              <AddIcon />
              <Text>New organization</Text>
            </MenuItem>
            <Separator orientation="horizontal" />
            <MenuItem
              onAction={async () => {
                await navigate({ to: '/settings/workspace' });
              }}
            >
              <AddMembersIcon />
              <Text>Invite members</Text>
            </MenuItem>
            <MenuItem
              onAction={async () => {
                await navigate({ to: '/settings/profile' });
              }}
            >
              <ProfileSettingsIcon />
              <Text>Profile settings</Text>
            </MenuItem>
            <MenuItem
              onAction={async () => {
                await navigate({ to: '/settings/workspace' });
              }}
            >
              <SettingsIcon />
              <Text>Account settings</Text>
            </MenuItem>
            <Separator orientation="horizontal" />
            <MenuItem
              onAction={async () => {
                await navigate({ to: '/logout' });
              }}
            >
              <LogoutIcon />
              <Text>Logout</Text>
            </MenuItem>
          </Menu>
        </Popover>
      </MenuTrigger>
    </div>
  );
};

export const WorkspaceCard = memo(IWorkspaceCard) as typeof IWorkspaceCard;
